import { createApp } from 'vue';
import { get } from 'lodash';
import { datadogRum } from '@datadog/browser-rum';
import Emitter from 'tiny-emitter';
import Highcharts from 'highcharts';
import StockModule from 'highcharts/modules/stock';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsVue from 'highcharts-vue';
import events from '@/mixins/events';
import draggable from 'vuedraggable';
import VueDraggableResizable from 'vue-draggable-resizable';
import { AgGridVue } from 'ag-grid-vue3';
import { useContextStore } from '@/stores/context';
import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useEventsStore } from '@/stores/events';
import piniaDefault from '@/stores';
import App from '@/App.vue';
import { createAppRouter } from '@/router';
import Axios from '@/plugins/axios';
import { isEmbedded } from '@/webshell-embed/embedded';
import { shellBridge } from '@/webshell-embed/platform-bridge';
import {
  installShellContributions,
  uninstallShellContributions,
} from '@/webshell-embed/shell-contributions';

// plugins
import { vuetify, pathToComponentName } from './plugins/vuetify/vuetify';
import i18n from './plugins/vue-i18n';

// local styles
import '@style/main.scss';

// Leaflet styles, required for map rendering.
import 'leaflet/dist/leaflet.css';
import 'leaflet.awesome-markers/dist/leaflet.awesome-markers.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';

// numeralJS locales
import '@/lang/numeral';

// AgGrid
import './plugins/ag-grid/ag-grid';

// In order to use Highcharts Stock we need to
// wrap Highcharts with the correct module:
StockModule(Highcharts);
HighchartsMore(Highcharts);

// datadog initialization function
const initialiseDatadog = context => {
  // initialise Datadog RUM if not disabled for environment
  if (!get(context.datadog[context.env], 'disabled')) {
    datadogRum.init({
      applicationId: context.datadog.applicationId,
      clientToken: context.datadog.clientToken,
      site: 'datadoghq.eu',
      service: 'assortment-webtool-vue3',
      env: context.env,
      version: context.versionTag,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackFrustrations: true,
      trackLongTasks: true,
      trackResources: true,
      enableExperimentalFeatures: ['clickmap'],
      allowedTracingUrls: [context.clientUrl],
    });
    datadogRum.startSessionReplayRecording();
    datadogRum.setGlobalContextProperty('subBrand', context.subBrand);
  }
};

/**
 * Build and mount the Assortment application, for both the standalone entry (`main.js`) and the
 * federated remote (`WebshellMain.vue`). Embedded, the shell owns the chrome and the router runs under
 * a tenant-scoped base; AO still loads its own auth and context either way.
 *
 * Whether AO is embedded is read from the `embedded` module, not passed in: the entry point settles
 * it before calling, along with the rest of the embedded-only setup (axios origin, shell base,
 * tenant seed) that needs to be in place before the first request goes out.
 *
 * @param {object}        options
 * @param {string|Element} options.mountTarget   selector or element to mount into
 * @param {string}         [options.routerBase]  history base (e.g. '/acme/assortment')
 * @param {string}         [options.initialPath] AO-internal path to open on, embedded only
 * @returns {{ app: import('vue').App, dispose: () => void, whenMounted: Promise<void> }} the created app;
 *   `dispose` to tear it down, safe before the app has mounted, in which case it never mounts; and
 *   `whenMounted`, which resolves once the app has mounted, or without mounting if disposed first,
 *   and rejects embedded if the context load fails
 */
export function createAssortmentApp({ mountTarget, routerBase, initialPath } = {}) {
  const embedded = isEmbedded();

  const router = createAppRouter(routerBase, { embedded });

  // Every .vue file, registered globally below.
  const vueComponents = import.meta.glob(['../src/**/*.vue'], { eager: true });

  const app = createApp(App);

  // TODO: vuetify - refactor mixins to composable
  // global mixins
  app.mixin(events);

  app.use(i18n);

  const emitter = new Emitter();
  app.config.globalProperties.emitter = emitter;

  app.use(HighchartsVue);

  app.use(vuetify);

  // Embedded, reuse the shell's Pinia so router guards read the same store the bootstrap
  // populates. Standalone uses AO's own pinia, with its persistedstate plugin.
  const pinia = (embedded && shellBridge()?.pinia) || piniaDefault;
  app.use(pinia);

  const contextStore = useContextStore();

  app.use(Axios, {
    router,
    store: {
      context: contextStore,
      scenarios: useScenariosStore(),
      workpackages: useWorkpackagesStore(),
    },
    i18n,
  });

  app.component('Draggable', draggable);
  app.component('VueDraggableResizable', VueDraggableResizable);

  // Register AG Grid globally
  app.component('AgGridVue', AgGridVue);

  Object.entries(vueComponents).forEach(([path, definition]) => {
    const componentName = pathToComponentName(path);

    app.component(componentName, definition.default);
  });

  // Embedded, the shell can unmount AO before the context loads (a quick click on to another
  // product), so every await re-checks `disposed`: mounting then would install AO on a page that has
  // left.
  let disposed = false;
  let mounted = false;

  const mountApp = async () => {
    if (disposed) return;
    // A memory history starts at '/', so seed the path the shell opened us on. Before
    // `app.use(router)`, which performs its own initial navigation only from START_LOCATION.
    if (embedded) {
      await router
        .replace(initialPath || '/')
        .catch(error => console.error('[assortment] failed to open', initialPath, error));
      if (disposed) return;
    }
    app.use(router);
    app.mount(mountTarget);
    mounted = true;
    initialiseDatadog(contextStore);

    // Everything AO publishes to the shell, and the shell language it follows. Here rather than in
    // WebshellMain.vue because it needs the catalogues loadUserContext fetches, and this block runs
    // once those have resolved.
    if (embedded) installShellContributions(router);
  };

  // Load app context+translations and the current profile, then install the router and mount.
  // Standalone, a failed load still mounts and the router guard sends the user to login. Embedded,
  // the shell owns login, so a failed load rejects without mounting and the shell shows its error.
  const whenMounted = Promise.all([
    contextStore.loadAppContext(),
    contextStore.loadUserContext(),
  ]).then(mountApp, error => {
    if (disposed) return undefined;
    if (embedded) throw error;
    return mountApp();
  });

  // Embedded, the shell's pinia, its bridge registries and the global axios all outlive the app, so
  // unmounting alone leaves AO's contributions, event stream and interceptors behind.
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    if (embedded) uninstallShellContributions();
    useEventsStore(pinia).stopEventStream();
    Axios.uninstall(app);
    if (mounted) app.unmount();
  };

  return { app, dispose, whenMounted };
}
